import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RevoGrid } from '@revolist/react-datagrid';
import type { ColumnData, ColumnProp } from '@revolist/react-datagrid';
import { Card, useTheme } from '@mui/material';

import {
  getFilteredProps,
  resolveFilterConfig,
  toMultiFilterItem,
  withFilterableColumns,
} from './filtering';
import {
  REVO_GRID_RANGE_FILL_PROPS,
  getOptionColumns,
  sanitizeRangeEditValues,
} from './rangeEditing';
import { RevoGridFilterPanel } from './RevoGridFilterPanel';
import { FIELD_HEIGHT, setEditorTheme } from './SelectCellEditor';
import { REVO_GRID_HEADER_SIZE, REVO_GRID_ROW_SIZE, revoGridTableSx } from './revoGridTableSx';
import { withSelectionColumn } from './selectionColumn';
import { keepSortDirection, withSortableColumns } from './sorting';
import { defaultGetRowId, useRevoGridSelection } from './useRevoGridSelection';
import {
  RevoGridBeforeRangeEditEvent,
  RevoGridBeforeSortingEvent,
  RevoGridColumnFilter,
  RevoGridColumnFilters,
  RevoGridFilterTarget,
  RevoGridTableExtras,
  RevoGridTableProps,
} from './types';

const DEFAULT_MIN_VISIBLE_ROWS = 4;
const DEFAULT_MAX_VISIBLE_ROWS = 10;

const getGroupingDepth = (columns: ColumnData): number =>
  columns.reduce(
    (depth, column) =>
      'children' in column ? Math.max(depth, 1 + getGroupingDepth(column.children)) : depth,
    0
  );

export const RevoGridTable = forwardRef<HTMLRevoGridElement, RevoGridTableProps>(
  (
    {
      columns,
      source,
      rowSize = REVO_GRID_ROW_SIZE,
      sortable = false,
      filterable = false,
      selectable = false,
      rangeFill = false,
      selectedIds,
      onSelectedIdsChange,
      getRowId = defaultGetRowId,
      height,
      minVisibleRows = DEFAULT_MIN_VISIBLE_ROWS,
      maxVisibleRows = DEFAULT_MAX_VISIBLE_ROWS,
      additionalData,
      sx,
      onBeforesorting,
      onBeforerangeedit,
      ...gridProps
    },
    ref
  ) => {
    const theme = useTheme();
    /**
     * Kept alongside the forwarded ref because `onOpenEditor` below needs the
     * grid API, and the call site may not pass a ref at all.
     */
    const gridRef = useRef<HTMLRevoGridElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLRevoGridElement | null) => {
        gridRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    /**
     * Cell editors render in their own React root and cannot read context, so
     * hand them the live theme. Safe in an effect: editors only mount on user
     * interaction, long after this has run.
     */
    useEffect(() => {
      setEditorTheme(theme);
    }, [theme]);

    const onOpenEditor = useCallback((rowIndex: number, prop: ColumnProp) => {
      void gridRef.current?.setCellEdit(rowIndex, prop);
    }, []);

    const { selection, syncVisibleIds } = useRevoGridSelection({
      gridRef,
      source,
      selectable,
      selectedIds,
      onSelectedIdsChange,
      getRowId,
    });

    /**
     * One condition per column, and the column whose panel is open. Both are
     * React's: the grid is told about a filter, it is not asked for one.
     */
    const [filters, setFilters] = useState<RevoGridColumnFilters>({});
    const [filterTarget, setFilterTarget] = useState<RevoGridFilterTarget | null>(null);
    const { operators, defaultOperator } = useMemo(
      () => resolveFilterConfig(filterable),
      [filterable]
    );
    const isFilterable = !!filterable;

    const applyFilters = useCallback((next: RevoGridColumnFilters) => {
      setFilters(next);
      gridRef.current?.dispatchEvent(
        new CustomEvent('filter', { detail: toMultiFilterItem(next) })
      );
    }, []);

    const onFilterChange = useCallback(
      (filter: RevoGridColumnFilter) => {
        if (filterTarget) {
          applyFilters({ ...filters, [filterTarget.prop]: filter });
        }
      },
      [applyFilters, filters, filterTarget]
    );

    const onFilterReset = useCallback(() => {
      if (!filterTarget) {
        return;
      }
      const next = { ...filters };
      delete next[filterTarget.prop];
      applyFilters(next);
    }, [applyFilters, filters, filterTarget]);

    const filteredProps = useMemo(() => getFilteredProps(filters), [filters]);

    const onBeforeSorting = useCallback(
      (event: RevoGridBeforeSortingEvent) => {
        onBeforesorting?.(event);
        keepSortDirection(event);
      },
      [onBeforesorting]
    );

    /**
     * Which columns only accept a value from a list. Derived from the columns
     * themselves, so a call site that adds a dropdown gets the guard below with
     * it.
     */
    const optionColumns = useMemo(() => getOptionColumns(columns), [columns]);

    const onBeforeRangeEdit = useCallback(
      (event: RevoGridBeforeRangeEditEvent) => {
        sanitizeRangeEditValues(event, optionColumns);
        onBeforerangeedit?.(event);
      },
      [optionColumns, onBeforerangeedit]
    );

    const gridExtras = useMemo<RevoGridTableExtras>(
      () => ({
        field: {
          border: theme.palette.divider,
          radius: `${theme.shape.borderRadius}px`,
          bg: theme.palette.background.default,
          text: theme.palette.text.primary,
          icon: theme.palette.action.active,
          height: `${FIELD_HEIGHT}px`,
          fontSize: `${theme.typography.body2.fontSize}`,
        },
        onOpenEditor,
        onOpenFilter: setFilterTarget,
        filteredProps,
        selection,
        ...additionalData,
      }),
      [theme, onOpenEditor, filteredProps, selection, additionalData]
    );

    const gridColumns = useMemo(() => {
      const selectableColumns = selectable ? withSelectionColumn(columns) : columns;
      const sortableColumns = sortable ? withSortableColumns(selectableColumns) : selectableColumns;
      return isFilterable ? withFilterableColumns(sortableColumns) : sortableColumns;
    }, [columns, selectable, sortable, isFilterable]);

    const selectionEvents = selectable
      ? ({ onAfterfilterapply: syncVisibleIds } as Record<string, unknown>)
      : {};

    const visibleRows = Math.min(Math.max(source?.length, minVisibleRows), maxVisibleRows);
    const headerRows = useMemo(() => 1 + getGroupingDepth(columns), [columns]);

    return (
      <Card
        sx={[
          revoGridTableSx(theme),
          {
            height: height ?? REVO_GRID_HEADER_SIZE * headerRows + rowSize * visibleRows,
            backgroundColor: 'background.default',
            overflow: 'hidden',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <RevoGrid
          ref={setRefs}
          theme={theme.palette.mode === 'dark' ? 'darkMaterial' : 'material'}
          columns={gridColumns}
          source={source}
          rowSize={rowSize}
          additionalData={gridExtras}
          filter={isFilterable}
          onBeforesorting={sortable ? onBeforeSorting : onBeforesorting}
          onBeforerangeedit={onBeforeRangeEdit}
          {...(rangeFill ? REVO_GRID_RANGE_FILL_PROPS : {})}
          {...selectionEvents}
          {...gridProps}
        />

        {isFilterable && (
          <RevoGridFilterPanel
            open={!!filterTarget}
            columnName={filterTarget?.name ?? ''}
            anchorPosition={{
              top: filterTarget?.anchor.bottom ?? 0,
              left: filterTarget?.anchor.left ?? 0,
            }}
            operators={operators}
            filter={
              (filterTarget && filters[filterTarget.prop]) ?? {
                operator: defaultOperator,
                value: '',
              }
            }
            onChange={onFilterChange}
            onReset={onFilterReset}
            onClose={() => setFilterTarget(null)}
          />
        )}
      </Card>
    );
  }
);
